import React, { useEffect, useRef, useState } from "react";
import { IBoltComment } from "../../types/IBoltComment";
import { BoltUserCommentExpressionSchema } from "../../types/BoltUserCommentExpressionSchema";
import { useComments } from "../../hooks/useComments";
import {
  useFieldSchemaV2,
  SchemaFactoryV2,
  FieldFormV2,
  FieldFormV2Module,
  SecondaryButton,
  AppToasterV2,
} from "@ucl/library";
import { useBoltApiClient } from "@bolt/core";
import { createBoltCommentFormBuilder } from "./builders/createBoltCommentFormBuilder";

import { faPaperPlane, faTimes } from "@fortawesome/free-solid-svg-icons";

export type ContentMode = "textOnly" | "htmlOnly" | "textOrHtml";

export interface InlineCommentEditorProps {
  comment?: IBoltComment;
  entityType?: string;
  entityId?: string;
  onSave?: (updatedComment: IBoltComment) => Promise<void>;
  onCommentCreated?: (comment: IBoltComment) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  className?: string;
  contentMode?: ContentMode;
}

export const InlineCommentEditor: React.FC<InlineCommentEditorProps> = ({
  comment,
  entityType,
  entityId,
  onSave,
  onCommentCreated,
  onCancel,
  isLoading = false,
  className = "",
  contentMode = "textOnly",
}) => {
  const commentsApi = useComments();
  const boltApiClient = useBoltApiClient();
  const { getAsyncSchemaFactory } = useFieldSchemaV2("comments/field-schema");
  const [schemaFactory, setSchemaFactory] = useState<SchemaFactoryV2>();
  const [formValue, setFormValue] = useState<BoltUserCommentExpressionSchema>({
    messageText: comment?.messageText || "",
    messageHtml: comment?.messageHtml || "",
    isPinned: comment?.isPinned || false,
    isHtml:
      comment?.isHtml ||
      (contentMode === "htmlOnly"
        ? true
        : contentMode === "textOnly"
          ? false
          : false),
  });
  const loadingRef = useRef<boolean>(false);
  const formRef =
    useRef<FieldFormV2Module<BoltUserCommentExpressionSchema>>(null);

  const isCreating = !comment;

  useEffect(() => {
    setFormValue((old) => ({
      ...old,
      isHtml:
        comment?.isHtml ||
        (contentMode === "htmlOnly"
          ? true
          : contentMode === "textOnly"
            ? false
            : false),
    }));
  }, [contentMode, comment]);

  const getSchema = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const factory = await getAsyncSchemaFactory(boltApiClient);
      setSchemaFactory(factory);
    } catch (err) {
      console.error("Failed to load schema:", err);
      AppToasterV2.showFailedActionToaster("Failed to load system");
    }
  };

  useEffect(() => {
    getSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormSubmit = async (value: BoltUserCommentExpressionSchema) => {
    if (!value.messageText?.trim() && !value.messageHtml?.trim()) {
      return;
    }

    try {
      if (isCreating) {
        if (!entityType || !entityId) {
          throw new Error(
            "Entity type and ID are required to create a comment"
          );
        }

        const newComment = await commentsApi.createComment({
          entityType,
          entityId,
          messageText: value.messageText,
          messageHtml: value.messageHtml || value.messageText,
          isPinned: value.isPinned,
          isHtml: value.isHtml,
        });

        await onCommentCreated?.(newComment);

        formRef.current?.reset();
      } else {
        const updatedComment = await commentsApi.updateComment(comment!.id, {
          messageText: value.messageText,
          messageHtml: value.messageHtml || value.messageText,
          isPinned: value.isPinned,
          isHtml: value.isHtml,
        });

        await onSave?.(updatedComment);
      }
    } catch (err) {
      console.error("Failed to save comment:", err);
      AppToasterV2.showFailedActionToaster("Failed to save comment. Please try again.");
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  if (!schemaFactory) {
    return (
      <div className={`bolt-inline-comment-editor ${className}`}>
        <div className="bolt-inline-comment-editor-loading">
          Loading editor...
        </div>
      </div>
    );
  }

  const fieldSchemaFormBuilder = createBoltCommentFormBuilder(
    schemaFactory,
    contentMode
  );

  return (
    <div className={`bolt-inline-comment-editor ${className}`}>
      <div className="bolt-inline-comment-editor-content">
        <FieldFormV2<BoltUserCommentExpressionSchema>
          value={formValue}
          fieldSchemaFormBuilder={fieldSchemaFormBuilder}
          onFormSubmit={handleFormSubmit}
          onFieldChanged={(newValue: BoltUserCommentExpressionSchema) => {
            setFormValue(newValue);
          }}
          isDisabled={isLoading}
          ref={formRef}
        />
      </div>

      <div className="bolt-inline-comment-editor-actions">
        {/* {onCancel && (
          <button
            type="button"
            className="bolt-inline-comment-editor-button bolt-inline-comment-editor-button-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faTimes} />
            Cancel
          </button>
        )} */}
        {/* <button
          type="submit"
          className="bolt-inline-comment-editor-button bolt-inline-comment-editor-button-primary"
          disabled={
            isLoading ||
            (!formValue.messageText?.trim() && !formValue.messageHtml?.trim())
          }
          onClick={() => formRef.current?.submit()}
        >
          <FontAwesomeIcon icon={faPaperPlane} />
          {isLoading ? "Saving..." : isCreating ? "Post Comment" : "Save"}
        </button> */}

        <SecondaryButton
          iconProps={{ icon: faPaperPlane }}
          onClick={() => formRef.current?.submit()}
          disabled={
            isLoading ||
            (!formValue.messageText?.trim() && !formValue.messageHtml?.trim())
          }
        />
        {!isCreating && (
          <SecondaryButton
            iconProps={{ icon: faTimes }}
            onClick={handleCancel}
            disabled={isLoading}
          />
        )}
      </div>
    </div>
  );
};
